import Close from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMemo, useState } from 'react'

import Chart from '@/components/chart'
import { orderTokensLikeBE } from '@/components/chart/utils'
import type { SwapTokenInfo } from '@/hooks/use-tokens'
import useTrans from '@/hooks/useTrans'
import { Candle } from '@/providers/liquidity-chart/candle-utils'
import { TradingViewTickerTimeFrame } from '@/utils/apis/tickers'
import { NOT_AVAILABLE_LOGO_URL } from '@/utils/helpers'

const CandleChartBox = ({
  tokenIn,
  tokenOut,
  onCandleListUpdated,
  onPriceSentFromSocket,
  onHideChart,
}: {
  tokenIn: SwapTokenInfo
  tokenOut: SwapTokenInfo
  onCandleListUpdated?: (candles: Candle[]) => void
  onPriceSentFromSocket?: ({ price }: { price: number }) => void
  onHideChart?: () => void
}) => {
  const trans = useTrans()

  const [base, quote] = useMemo(
    () => (tokenIn && tokenOut ? orderTokensLikeBE(tokenIn, tokenOut) : [undefined, undefined]),
    [tokenIn, tokenOut],
  )

  const [timeFrame, setTimeFrame] = useState<TradingViewTickerTimeFrame>('one_hour')

  if (!base || !quote) return <></>

  return (
    <Box
      className="bg-[#1E2735] rounded-t-3xl sm:rounded-b-3xl pb-[100%] lg:pb-0 relative"
      data-testid="chart"
      width={{ xs: '100%', lg: '55%' }}
    >
      <Stack className="absolute inset-0 py-6 pl-6 pr-4 max-h-[50vh]" spacing={1}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Stack direction="row" spacing={-1}>
              <img
                data-testid="chart-token-in-logo"
                src={base.logoURI || NOT_AVAILABLE_LOGO_URL}
                alt={base.displaySymbol ?? 'Unknown'}
                className="rounded-full w-6 h-6"
              />
              <img
                data-testid="chart-token-out-logo"
                src={quote.logoURI || NOT_AVAILABLE_LOGO_URL}
                alt={quote.displaySymbol ?? 'Unknown'}
                className="rounded-full w-6 h-6"
              />
            </Stack>
            <Typography className="text-sm font-semibold" data-testid="chart-token-pair">
              {base.displaySymbol ?? 'Unknown'}/{quote.displaySymbol ?? 'Unknown'}
            </Typography>
          </Stack>
          {onHideChart && (
            <IconButton
              data-testid="chart-close-btn"
              onClick={onHideChart}
              title={trans.limit.hide_chart_btn}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <Box className="flex-grow">
          <Chart
            timeFrame={timeFrame}
            tokenA={base}
            tokenB={quote}
            onCandleListUpdated={onCandleListUpdated}
            onPriceSentFromSocket={onPriceSentFromSocket}
            onTimeFrameChange={setTimeFrame}
          />
        </Box>
      </Stack>
    </Box>
  )
}

export default CandleChartBox
